import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { inputClass } from "../forms/FormKit";

const TOGGLES = [
  ["notify_on_enquiry", "Email an alert for new business requests"],
  ["notify_on_application", "Email an alert for new expert applications"],
  ["notify_on_message", "Email an alert for new contact messages"],
  ["send_confirmations", "Send a receipt confirmation to submitters (reference number only)"],
];

const StatusLine = ({ ok, label, help }) => (
  <div className="flex gap-3 text-sm">
    {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
    <div><p className="font-medium text-[#0B1220]">{label}: {ok ? "configured" : "not configured"}</p>{!ok && <p className="mt-1 text-slate-500">{help}</p>}</div>
  </div>
);

export const SettingsPanel = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const save = async () => {
    setBusy("save");
    try {
      const { email_configured, storage_configured, updated_at, updated_by, ...body } = s;
      const { data } = await api.put("/admin/settings", { ...body, retention_days: Number(body.retention_days) });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const testEmail = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/settings/test-email");
      data.status === "sent" ? toast.success(`Test email accepted for delivery to ${data.to}`) : toast.error(`Test email not sent: ${data.error || data.status}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const retention = async () => {
    if (!window.confirm(`Permanently delete archived records older than ${s.retention_days} days? This cannot be undone.`)) return;
    setBusy("retention");
    try {
      const { data } = await api.post("/admin/retention/apply");
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Retention applied: ${total} archived record(s) deleted`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="settings-panel">
      <section className="rounded-md border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-semibold text-[#0B1220]">Notifications</h2>
        <label htmlFor="notify-email" className="mt-6 block text-sm font-medium text-[#111827]">Notification recipient</label>
        <input id="notify-email" type="email" value={s.notify_email} onChange={(e) => setS({ ...s, notify_email: e.target.value })} className={`${inputClass(false)} mt-2`} data-testid="settings-notify-email-input" />
        <div className="mt-6 space-y-4">
          {TOGGLES.map(([k, l]) => (
            <label key={k} className="flex items-start gap-3 text-sm text-slate-600">
              <input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-testid={`settings-${k}`} /> {l}
            </label>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button type="button" onClick={save} disabled={!!busy} className="btn-primary h-10 px-5 text-sm" data-testid="settings-save-button">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save settings</button>
          <button type="button" onClick={testEmail} disabled={!!busy} className="btn-secondary h-10 px-5 text-sm" data-testid="settings-test-email-button">{busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />} Send test email</button>
        </div>
      </section>
      <div className="space-y-6">
        <section className="rounded-md border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-semibold text-[#0B1220]">Data retention</h2>
          <label htmlFor="retention-days" className="mt-6 block text-sm font-medium text-[#111827]">Delete archived records after (days)</label>
          <input id="retention-days" type="number" min="30" max="3650" value={s.retention_days} onChange={(e) => setS({ ...s, retention_days: e.target.value })} className={`${inputClass(false)} mt-2 max-w-[200px]`} data-testid="settings-retention-input" />
          <p className="mt-2 text-xs text-slate-500">Only archived records are affected. Save settings before applying.</p>
          <button type="button" onClick={retention} disabled={!!busy} className="btn-secondary mt-6 h-10 px-5 text-sm" data-testid="settings-apply-retention-button">{busy === "retention" && <Loader2 className="h-4 w-4 animate-spin" />} Apply retention policy now</button>
        </section>
        <section className="space-y-4 rounded-md border border-slate-200 bg-white p-6" data-testid="settings-integrations">
          <h2 className="font-display text-lg font-semibold text-[#0B1220]">Integrations</h2>
          <StatusLine ok={s.email_configured} label="Email (Resend via Emergent)" help="Set EMERGENT_EMAIL_KEY and EMAIL_FROM_NAME in the backend environment, then restart." />
          <StatusLine ok={s.storage_configured} label="Private file storage" help="Set EMERGENT_LLM_KEY in the backend environment to enable private CV storage." />
        </section>
      </div>
    </div>
  );
};
